/**
 * SubBatchProgress Component
 *
 * Displays detailed sub-batch progress for feed import runs.
 * Shows phase-by-phase breakdown with ETA calculation.
 *
 * Date: 2026-03-07
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  RefreshCw,
  Clock,
  CheckCircle,
  Loader2,
  AlertCircle,
  Sparkles,
  Database,
  FileText,
  Users,
  Hourglass,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { formatDistanceToNow } from 'date-fns';

interface PhaseBreakdown {
  enrichment: PhaseStats;
  benefit_scoring: PhaseStats;
  embedding: PhaseStats;
  waiting_for_cohort: PhaseStats;
  aeo_generation: PhaseStats;
  aeo_validation: PhaseStats;
  completed: PhaseStats;
}

interface PhaseStats {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

interface Timing {
  started_at: string | null;
  eta: string | null;
  eta_formatted: string | null;
  completed_batches: number;
  total_batches: number;
  total_products: number;
  processed_products: number;
}

interface SubBatchProgressData {
  summary: {
    total: number;
    completed: number;
    processing: number;
    pending: number;
    failed: number;
    cancelled: number;
    currentPhases: string[];
  };
  phase_breakdown: PhaseBreakdown;
  timing: Timing;
  sub_batches: SubBatch[];
}

interface SubBatch {
  id: string;
  batch_number: number;
  product_count: number;
  current_phase: string;
  status: string;
  enrichment_succeeded: number | null;
  enrichment_failed: number | null;
  embedding_succeeded: number | null;
  embedding_failed: number | null;
  aeo_generated: number | null;
  aeo_validated: number | null;
  error_message: string | null;
}

interface SubBatchProgressProps {
  runId: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

const PHASE_ORDER = [
  'enrichment',
  'benefit_scoring',
  'embedding',
  'waiting_for_cohort',
  'aeo_generation',
  'aeo_validation',
  'completed',
] as const;

const PHASE_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; bgColor: string }> = {
  enrichment: {
    label: 'AI Enrichment',
    icon: <Sparkles className="h-4 w-4" />,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
  },
  benefit_scoring: {
    label: 'Benefit Scoring',
    icon: <Users className="h-4 w-4" />,
    color: 'text-pink-600',
    bgColor: 'bg-pink-100 dark:bg-pink-900/30',
  },
  embedding: {
    label: 'Embeddings',
    icon: <Database className="h-4 w-4" />,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
  },
  waiting_for_cohort: {
    label: 'Waiting for Cohort',
    icon: <Hourglass className="h-4 w-4" />,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
  },
  aeo_generation: {
    label: 'AEO Generation',
    icon: <FileText className="h-4 w-4" />,
    color: 'text-violet-600',
    bgColor: 'bg-violet-100 dark:bg-violet-900/30',
  },
  aeo_validation: {
    label: 'Validation',
    icon: <CheckCircle className="h-4 w-4" />,
    color: 'text-green-600',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  },
  completed: {
    label: 'Completed',
    icon: <CheckCircle className="h-4 w-4" />,
    color: 'text-green-600',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  },
};

export function SubBatchProgress({ runId, autoRefresh = true, refreshInterval = 10000 }: SubBatchProgressProps) {
  const [data, setData] = useState<SubBatchProgressData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchProgress = useCallback(async () => {
    try {
      const response = await authFetch(`/feed-imports/runs/${runId}/sub-batches`);
      if (!response.ok) {
        throw new Error('Failed to fetch sub-batch progress');
      }
      const result = await response.json();
      setData(result);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    fetchProgress();

    if (autoRefresh) {
      const interval = setInterval(fetchProgress, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchProgress, autoRefresh, refreshInterval]);

  if (loading && !data) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Sub-Batch Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-500" />
            Sub-Batch Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-red-600">{error}</div>
          <Button variant="outline" size="sm" className="mt-2" onClick={fetchProgress}>
            <RefreshCw className="h-3 w-3 mr-1" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { summary, phase_breakdown, timing } = data;
  // Calculate progress based on phases completed, not just fully-completed batches
  // A batch goes through: enrichment → benefit_scoring → embedding → waiting_for_cohort → aeo_generation → validation → completed
  // We weight progress by how far along batches are in the pipeline
  const calculateWeightedProgress = () => {
    if (timing.total_batches === 0) return 0;

    const weights: Record<string, number> = {
      enrichment: 0,
      benefit_scoring: 15,
      embedding: 30,
      waiting_for_cohort: 50, // Significant progress - pre-AEO complete
      aeo_generation: 70,
      aeo_validation: 90,
      completed: 100,
    };

    let totalWeight = 0;
    for (const [phase, stats] of Object.entries(phase_breakdown)) {
      const phaseWeight = weights[phase] || 0;
      const phaseBatches = (stats.processing || 0) + (stats.completed || 0) + (stats.pending || 0);
      totalWeight += phaseBatches * phaseWeight;
    }

    return Math.round(totalWeight / timing.total_batches);
  };

  const overallProgress = calculateWeightedProgress();

  // Calculate which phases have activity
  const activePhases = PHASE_ORDER.filter(phase => {
    const stats = phase_breakdown[phase];
    return stats && (stats.pending > 0 || stats.processing > 0 || stats.completed > 0 || stats.failed > 0);
  });

  // Find current phase (first phase with processing batches)
  const currentPhase = PHASE_ORDER.find(phase => {
    const stats = phase_breakdown[phase];
    return stats && stats.processing > 0;
  }) || (timing.completed_batches === timing.total_batches ? 'completed' : 'enrichment');

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Loader2 className={`h-4 w-4 ${timing.completed_batches < timing.total_batches ? 'animate-spin' : ''}`} />
            Sub-Batch Progress
          </CardTitle>
          <div className="flex items-center gap-2">
            {timing.eta_formatted && (
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {timing.eta_formatted}
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={fetchProgress} disabled={loading}>
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overall Progress */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>
              {/* Show batches past enrichment as meaningful progress indicator */}
              {(phase_breakdown.embedding?.processing || 0) +
               (phase_breakdown.embedding?.completed || 0) +
               (phase_breakdown.waiting_for_cohort?.processing || 0) +
               (phase_breakdown.waiting_for_cohort?.completed || 0) +
               (phase_breakdown.aeo_generation?.processing || 0) +
               (phase_breakdown.aeo_generation?.completed || 0) +
               (phase_breakdown.completed?.completed || 0)} of {timing.total_batches} batches past enrichment
            </span>
            <span>{overallProgress}%</span>
          </div>
          <Progress value={overallProgress} className="h-2" />
          <div className="text-xs text-muted-foreground">
            {timing.total_products.toLocaleString()} products total
          </div>
        </div>

        {/* Phase Pipeline */}
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Pipeline Phases
          </div>
          <div className="space-y-1.5">
            {PHASE_ORDER.map((phase) => {
              const stats = phase_breakdown[phase];
              if (!stats) return null;

              const total = stats.pending + stats.processing + stats.completed + stats.failed;
              if (total === 0 && phase !== currentPhase) return null;

              const config = PHASE_CONFIG[phase];
              const isActive = stats.processing > 0;
              const isComplete = stats.completed > 0 && stats.processing === 0 && stats.pending === 0;
              const hasFailed = stats.failed > 0;

              return (
                <div
                  key={phase}
                  className={`flex items-center gap-3 p-2 rounded-md transition-colors ${
                    isActive ? config.bgColor : 'bg-muted/30'
                  }`}
                >
                  <div className={`${isActive ? config.color : 'text-muted-foreground'}`}>
                    {isActive ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : isComplete ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      config.icon
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${isActive ? config.color : 'text-muted-foreground'}`}>
                        {config.label}
                      </span>
                      <div className="flex items-center gap-1.5 text-xs">
                        {stats.processing > 0 && (
                          <Badge variant="secondary" className="text-xs px-1.5 py-0">
                            {stats.processing} processing
                          </Badge>
                        )}
                        {stats.completed > 0 && (
                          <Badge variant="outline" className="text-xs px-1.5 py-0 text-green-600 border-green-200">
                            {stats.completed} done
                          </Badge>
                        )}
                        {stats.pending > 0 && (
                          <Badge variant="outline" className="text-xs px-1.5 py-0">
                            {stats.pending} pending
                          </Badge>
                        )}
                        {hasFailed && (
                          <Badge variant="destructive" className="text-xs px-1.5 py-0">
                            {stats.failed} failed
                          </Badge>
                        )}
                      </div>
                    </div>
                    {total > 0 && (
                      <Progress
                        value={Math.round(((stats.completed + stats.failed) / total) * 100)}
                        className="h-1 mt-1"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
          <div className="flex items-center gap-4">
            <span>
              <span className="font-medium text-foreground">{summary.processing}</span> processing
            </span>
            <span>
              <span className="font-medium text-foreground">{summary.pending}</span> pending
            </span>
            {summary.failed > 0 && (
              <span className="text-red-600">
                <span className="font-medium">{summary.failed}</span> failed
              </span>
            )}
          </div>
          {lastUpdated && (
            <span>Updated {formatDistanceToNow(lastUpdated, { addSuffix: true })}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default SubBatchProgress;
